import React, { useCallback, useEffect, useRef, useContext } from 'react';

import WebcastingContext from '../../../context/webcasting/webcastingContext';

import { gaEvent } from '../../../helpers/reactGA';
import {
  getLiveMutePref,
  resolveLiveMutePref,
  setLiveMutePref,
} from '../../../helpers/liveMutePreference';

/** Wowza-hosted Flowplayer module for this stream/embed (from Wowza embed snippet). */
const WOWZA_FLOWPLAYER_MODULE =
  'https://embed.wowza.com/cbcf073c-7d74-4bb8-8bdb-91c409194ba8.js';

const VideoJsSimpleFunc = ({ src }) => {
  const webcastingContext = useContext(WebcastingContext);
  const { hasMetaData, QnaStatus, event } = webcastingContext;

  const playerIdRef = useRef(
    `player-${Math.random().toString(36).slice(2, 11)}`,
  );
  const moduleScriptRef = useRef(null);
  const hasMetaDataRef = useRef(hasMetaData);
  hasMetaDataRef.current = hasMetaData;

  const eventIdRef = useRef(event?._id);
  eventIdRef.current = event?._id;

  const videoElRef = useRef(null);
  const pendingMutedRef = useRef(null);
  const applyingPrefRef = useRef(false);
  const prevQnaStatusRef = useRef(null);

  // Sets mute without recording it as a viewer choice. Queues the value when
  // Flowplayer has not injected the video element yet.
  const applyMuted = useCallback((muted) => {
    const v = videoElRef.current;
    if (!v) {
      pendingMutedRef.current = muted;
      return;
    }

    pendingMutedRef.current = null;
    if (v.muted === muted) return;

    applyingPrefRef.current = true;
    v.muted = muted;
    // volumechange lands in a later task, so hold the guard until then.
    setTimeout(() => {
      applyingPrefRef.current = false;
    }, 0);
  }, []);

  useEffect(() => {
    const id = playerIdRef.current;
    const container = document.getElementById(id);
    if (!container || !src) return undefined;

    const flowplayerConfig = {
      src,
      autoplay: true,
      muted: true,
    };

    const script = document.createElement('script');
    script.type = 'module';
    script.textContent = `
  import flowplayer from ${JSON.stringify(WOWZA_FLOWPLAYER_MODULE)};
flowplayer(${JSON.stringify(`#${id}`)}, ${JSON.stringify(flowplayerConfig)});
`;
    document.body.appendChild(script);
    moduleScriptRef.current = script;

    const onLoadedMetadata = (e) => {
      const fn = hasMetaDataRef.current;
      if (typeof fn === 'function') fn(e);
    };

    const onPlay = () => {
      const v = container.querySelector('video');
      const t = v ? `${v.currentTime}` : '0';
      gaEvent('video', 'play', t);
    };

    const onPause = () => {
      const v = container.querySelector('video');
      const t = v ? `${v.currentTime}` : '0';
      gaEvent('video', 'pause', t);
    };

    const onVolumeChange = () => {
      const v = container.querySelector('video');
      if (!v) return;
      if (applyingPrefRef.current) return;
      if (v.volume === 0) {
        gaEvent('video', 'volume change', '0');
        gaEvent('volume change', 'video', '0');
      }
      v.muted
        ? gaEvent('video', 'mute', `${v.currentTime}`)
        : gaEvent('video', 'unmute', `${v.currentTime}`);
      setLiveMutePref(eventIdRef.current, v.muted);
    };

    const bindVideo = (v) => {
      v.setAttribute('playsinline', '');
      v.playsInline = true;
      v.addEventListener('loadedmetadata', onLoadedMetadata);
      v.addEventListener('play', onPlay);
      v.addEventListener('pause', onPause);
      v.addEventListener('volumechange', onVolumeChange);
      videoElRef.current = v;
      if (pendingMutedRef.current !== null) {
        applyMuted(pendingMutedRef.current);
      } else if (getLiveMutePref(eventIdRef.current) === false) {
        applyMuted(false);
      }
    };

    const unbindVideo = (v) => {
      v.removeEventListener('loadedmetadata', onLoadedMetadata);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('volumechange', onVolumeChange);
      if (videoElRef.current === v) videoElRef.current = null;
    };

    let videoEl = null;
    const observer = new MutationObserver(() => {
      const v = container.querySelector('video');
      if (!v || v === videoEl) return;
      if (videoEl) {
        unbindVideo(videoEl);
      }
      videoEl = v;
      bindVideo(v);
    });

    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (videoEl) {
        unbindVideo(videoEl);
        videoEl = null;
      }
      if (moduleScriptRef.current && moduleScriptRef.current.parentNode) {
        moduleScriptRef.current.parentNode.removeChild(
          moduleScriptRef.current,
        );
      }
      moduleScriptRef.current = null;
      pendingMutedRef.current = null;
      container.replaceChildren();
    };
  }, [src, applyMuted]);

  // Operator full screen (on or off) follows the viewer's own mute choice
  // instead of forcing audio on.
  useEffect(() => {
    const previous = prevQnaStatusRef.current;
    const current = Boolean(QnaStatus);
    prevQnaStatusRef.current = current;

    if (previous === null) {
      // Joining while full screen is already on: only restore an explicit
      // unmute, never fight the muted autoplay start.
      if (current && getLiveMutePref(eventIdRef.current) === false) {
        applyMuted(false);
      }
      return;
    }

    if (previous === current) return;

    applyMuted(resolveLiveMutePref(eventIdRef.current));
  }, [QnaStatus, applyMuted]);

  return <div id={playerIdRef.current} style={{ width: '100%', height: '100%' }} />;
};

export default VideoJsSimpleFunc;
